import React from 'react';

import { margin, padding } from '@lumapps/classnames';
import { SelectTextField } from '@lumapps/combobox/components/SelectTextField/SelectTextField';
import { ServiceNotAvailableState } from '@lumapps/lumx-states/components/ServiceNotAvailableState';
import { mdiBullhorn } from '@lumapps/lumx/icons';
import { Size, Dialog, Button, Toolbar, FlexBox, Orientation, Alignment, Emphasis, Text } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { ProgramIdsByName } from '../../ducks/ProgramSelectionModal/slice';
import { SA_PROGRAMS } from '../../keys';

export interface ProgramSelectionModalProps {
    // Whether the modal is closed or open
    isOpen: boolean;
    // Whether the modal is loading, loaded, or has errors
    modalStatus: BaseLoadingStatus;
    // The list of program Ids to select within
    programIds: string[];
    // The dictionnary of ids by their names. Name is the key bc it's faster than the opposite as we search by name.
    programIdsByName: ProgramIdsByName;
    // The function to fetch the Program names
    fetchProgramsByIds(programIds: string[]): any;
    // The callback when a program has been selected
    onProgramSelected(programId: string): void;
    // The callback when the user cancels
    onCancel(): void;
}

const CLASSNAME = 'program-selection-modal';

export const ProgramSelectionModal: React.FC<ProgramSelectionModalProps> = ({
    modalStatus,
    programIds,
    programIdsByName,
    fetchProgramsByIds,
    onProgramSelected,
    onCancel,
    isOpen,
}) => {
    const { translateKey } = useTranslate();

    // Whether we should show the suggestion list
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const [value, setValue] = React.useState<string | undefined>();
    const [searchValue, setSearchValue] = React.useState<string>('');

    // When programIds changes, load the program names from backend and set the dictionary.
    React.useEffect(() => {
        if (isOpen && showSuggestions && modalStatus === BaseLoadingStatus.initial) {
            fetchProgramsByIds(programIds);
        }
    }, [programIds, modalStatus, fetchProgramsByIds, isOpen, showSuggestions]);

    const handleProgramSelected = () => {
        setValue(undefined);
        setShowSuggestions(false);
        if (value) {
            onProgramSelected(programIdsByName[value]);
        }
    };

    const handleCancel = () => {
        setValue(undefined);
        setShowSuggestions(false);
        onCancel();
    };

    // The list of programs that corresponding to the search text
    const filteredItems = Object.keys(programIdsByName).filter(
        (programName: string) => programName?.toLowerCase().indexOf(searchValue?.toLowerCase()) > -1,
    );

    return (
        <Dialog
            className={CLASSNAME}
            isOpen={isOpen}
            isLoading={modalStatus === BaseLoadingStatus.loading}
            size={Size.tiny}
            preventAutoClose
        >
            <Toolbar
                label={
                    <Text as="span" typography="title">
                        {translateKey(SA_PROGRAMS.SELECT_A_PROGRAM)}
                    </Text>
                }
            />

            {modalStatus === BaseLoadingStatus.error ? (
                <ServiceNotAvailableState />
            ) : (
                <SelectTextField<string>
                    selectionType="single"
                    autoFilter={false}
                    value={value}
                    options={filteredItems}
                    className={padding('horizontal', 'huge')}
                    onFocus={() => setShowSuggestions(true)}
                    label={translateKey(SA_PROGRAMS.SELECT_A_PROGRAM)}
                    icon={mdiBullhorn}
                    status={modalStatus}
                    onSearch={setSearchValue}
                    getOptionId={(option) => option}
                    onChange={setValue}
                />
            )}

            <FlexBox className={padding('all', 'huge')} orientation={Orientation.horizontal} vAlign={Alignment.right}>
                <Button onClick={handleCancel} emphasis={Emphasis.medium} className={margin('right')}>
                    {translateKey(GLOBAL.CANCEL)}
                </Button>
                <Button onClick={handleProgramSelected} isDisabled={value === undefined}>
                    {translateKey(SA_PROGRAMS.SELECT_PROGRAM)}
                </Button>
            </FlexBox>
        </Dialog>
    );
};
